/** FC246/#1283: the database batch owns unit cost; UI never silently approves it. */
import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { fetchTrainerBatchCostAudit, saveTrainerBatchCost } from "./trainerInventoryApi";

export const validBatchCost = (value) => /^\d+(?:\.\d{1,4})?$/.test(String(value).trim()) &&
  Number.isFinite(Number(value)) && Number(value) <= 999999.9999;

export const batchCostReady = (batch) => Boolean(batch?.batch_cost_reviewed_at) &&
  batch?.batch_unit_cost != null;

const operationId = () => `batch-cost-${typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`}`;

export default function TrainerBatchCostReview({ batch, disabled, onSaved }) {
  const [draft, setDraft] = useState("");
  const [reason, setReason] = useState("");
  const [approved, setApproved] = useState(false);
  const [zeroApproved, setZeroApproved] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [history, setHistory] = useState(null);
  const flight = useRef(false);
  const ready = batchCostReady(batch);
  const savedCost = batch?.batch_unit_cost == null ? null : Number(batch.batch_unit_cost).toFixed(4);
  const version = Number(batch?.batch_cost_version || 0);

  useEffect(() => {
    setDraft(batch?.batch_unit_cost == null ? "" : Number(batch.batch_unit_cost).toFixed(4));
    setReason(""); setApproved(false); setZeroApproved(false); setPending(null);
    setError(""); setSuccess(""); setHistory(null);
  }, [batch?.id, version]);

  const canSave = Boolean(batch) && batch.status !== "cancelled" && !disabled && !busy &&
    validBatchCost(draft) && approved && reason.trim().length >= 3 && reason.trim().length <= 500 &&
    (Number(draft) !== 0 || zeroApproved) && (!ready || Number(draft) !== Number(savedCost));

  const save = async () => {
    if (flight.current || (!canSave && !pending)) return;
    const request = pending || { unit_cost: draft.trim(), reason: reason.trim(),
      expected_version: version, operation_id: operationId(), confirm_zero: Number(draft) === 0 && zeroApproved };
    flight.current = true; setBusy(true); setError(""); setSuccess(""); setPending(request);
    try {
      const response = await saveTrainerBatchCost(batch.id, request);
      if (!response.data?.success) throw new Error("Batch cost update was not confirmed");
      setPending(null);
      setSuccess(`Reviewed batch COGS saved at AU$${Number(request.unit_cost).toFixed(4)}. Cost adjustment audit recorded.`);
      await onSaved(batch.id);
    } catch (e) {
      const status = e?.response?.status;
      setError(`${e?.response?.data?.error || e.message || "Update unconfirmed"}${!status || status >= 500
        ? ". The write might have succeeded. Retry the SAME operation ID; never create a duplicate adjustment." : ""}`);
      if (status >= 400 && status < 500) setPending(null);
    } finally { flight.current = false; setBusy(false); }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetchTrainerBatchCostAudit(batch.id);
      setHistory(response.data?.events || []);
    } catch (e) { setError(e?.response?.data?.error || "Could not load cost history"); }
  };

  if (!batch) return null;
  return <Box sx={{ border: 1, borderColor: "divider", p: 2, borderRadius: 1 }}>
    <Stack spacing={1.5}>
      <Typography variant="h6">Batch unit COGS — reviewed once, editable with audit</Typography>
      <Alert severity={ready ? "info" : "warning"}>
        {ready ? `Saved batch unit cost: AU$${savedCost} (version ${version}). Restored whenever you return to this batch.` :
          "This batch has no reviewed unit cost. Confirm a cost before staging cards. Each new batch needs its own cost."}
      </Alert>
      <Typography variant="body2">Changing this cost also revalues already-finalised receipt lots and any sold cards with verified order-line attribution. An untraceable sale or lot blocks the entire change without partial updates.</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField size="small" label="Batch unit cost (AUD)" type="number" inputProps={{ min: 0, step: "0.0001" }}
          value={draft} disabled={disabled || busy || Boolean(pending) || batch.status === "cancelled"}
          onChange={(e) => { setDraft(e.target.value); setApproved(false); setZeroApproved(false); }} />
        <TextField size="small" fullWidth label="Reason for batch cost assignment / change"
          value={reason} disabled={disabled || busy || Boolean(pending) || batch.status === "cancelled"}
          onChange={(e) => { setReason(e.target.value); setApproved(false); }} inputProps={{ maxLength: 500 }} />
      </Stack>
      <FormControlLabel control={<Checkbox checked={approved} disabled={disabled || busy || Boolean(pending) || !validBatchCost(draft)}
        onChange={e => setApproved(e.target.checked)} />} label="I reviewed this batch cost and approve applying it to every card in the batch" />
      {Number(draft) === 0 && <FormControlLabel control={<Checkbox checked={zeroApproved} disabled={disabled || busy || Boolean(pending)}
        onChange={e => setZeroApproved(e.target.checked)} />} label="I explicitly confirm that the correct batch cost is AU$0.0000" />}
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button variant="contained" disabled={!canSave || Boolean(pending)} onClick={save}>
          {ready ? "Confirm batch cost change" : "Set and review batch cost"}
        </Button>
        {pending && <Button variant="outlined" disabled={busy} onClick={save}>Retry same cost update</Button>}
        <Button variant="outlined" disabled={busy} onClick={fetchHistory}>View batch cost audit</Button>
      </Stack>
      {history && <Box>
        {history.length === 0 ? <Typography variant="body2">No batch cost changes recorded.</Typography> :
          history.map((event) => <Typography key={event.id} variant="body2">
            Version {event.batch_version}: {event.old_cost == null ? "unassigned" : `AU$${event.old_cost}`} → AU${event.new_cost} — {event.reason} ({event.actor}; {event.created_at})
          </Typography>)}
      </Box>}
    </Stack>
  </Box>;
}
